import { type ReactNode, useCallback, useId, useState } from 'react';

import { cn } from '@shared/lib/cn';
import { Button, type ButtonStatus, type ButtonVariant } from '@shared/ui/button';

import {
  DIALOG_CANCEL_BUTTON_CLASS,
  DIALOG_MAIN_BUTTON_CLASS,
  DIALOG_MAIN_BUTTON_STYLE,
  DIALOG_PANEL_CLASS,
  DIALOG_WIDTH_CLASS,
  type DialogSize,
  type DialogStatus,
} from './dialog.constant';
import { DIALOG_TEXT } from './dialog.text';
import { DialogParts } from './dialog-parts.ui';
import { ModalLayer } from './modal-layer.ui';

export interface DialogProps {
  /** 제어 prop. 비제어는 `defaultOpen` */
  open?: boolean;
  defaultOpen?: boolean;
  /** 닫기(×) · 취소 · Esc · 배경을 누르면 `false` 로 부른다 */
  onOpenChange?: (open: boolean) => void;
  /** Figma `Title` */
  title: ReactNode;
  /** Figma `Body` + `Show body text`. 제목 아래 설명 글(`aria-describedby`) */
  body?: ReactNode;
  /** Figma `Content` + `Show content`. 본문의 내용 자리(폼 등) */
  children?: ReactNode;
  /** Figma `size`(너비 xs 400 · sm 600 · md 800 · lg 1200). 기본 md */
  size?: DialogSize;
  /** Figma `status`. init = 제목 · 본문 막대, loading = 흐리게 + Spinner(누를 수 없다) */
  status?: DialogStatus;
  /** Figma `Show footer`. 기본 true */
  showFooter?: boolean;
  /** Figma `Show cancel`. 기본 true */
  showCancel?: boolean;
  /** Figma `Show danger`(수정 팝업의 "삭제하기"). 기본 false */
  showDanger?: boolean;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  dangerLabel?: ReactNode;
  confirmDisabled?: boolean;
  /** 확인 버튼의 Button `status`. loading = Spinner 를 겹치고 누를 수 없다(저장 중). 기본 default */
  confirmStatus?: ButtonStatus;
  /** 확인 버튼의 Button `variant`. 기본 primary(outline 등) */
  confirmVariant?: ButtonVariant;
  /** 머리 오른쪽(닫기 왼쪽)의 버튼 · 범례 등. 머리와 함께 고정된다 */
  headerActions?: ReactNode;
  /** 머리 아래 고정 줄(필터 · 탭 등). 본문이 스크롤돼도 남는다 */
  subheader?: ReactNode;
  /**
   * 본문에 붙는다(간격 등). 본문은 늘 넘치면 혼자 세로로 스크롤된다 — 머리 · 머리 아래 줄 · 바닥은 고정.
   * portal 이면 판 높이가 화면 − 위아래 20 으로 제한된다. inline 에서 높이를 정하려면 `max-h-(--size-…)` 를 준다
   */
  bodyClassName?: string;
  /** 확인. 닫는 것은 부르는 쪽이 정한다(`onOpenChange` 또는 `open`) */
  onConfirm?: () => void;
  /** 취소. 부른 뒤 닫는다 */
  onCancel?: () => void;
  onDanger?: () => void;
  /** 배경을 누르면 닫는다. 기본 true */
  closeOnBackdrop?: boolean;
  /** portal 대신 제자리에 그린다(fe-rules 3절). 확인 페이지는 inline 으로 늘어놓는다 */
  inline?: boolean;
  className?: string;
}

/**
 * D3 팝업. 머리(제목 + 닫기) · 본문(설명 글 + 내용 자리) · 바닥(취소 · 확인 · 삭제하기, 가운데 정렬).
 * 머리 · 바닥은 고정, 본문만 스크롤(판은 화면 높이 − 바깥 여백 안).
 * `role="dialog"` + `aria-modal`, 제목 · 설명 연결, 포커스 가두기, Esc · 배경으로 닫기.
 * 어두운 배경막은 portal 로 띄울 때만 그린다.
 */
export function Dialog({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  title,
  body,
  children,
  size = 'md',
  status = 'default',
  showFooter = true,
  showCancel = true,
  showDanger = false,
  confirmLabel = DIALOG_TEXT.confirm,
  cancelLabel = DIALOG_TEXT.cancel,
  dangerLabel = DIALOG_TEXT.danger,
  confirmDisabled = false,
  confirmStatus = 'default',
  confirmVariant = 'primary',
  headerActions,
  subheader,
  bodyClassName,
  onConfirm,
  onCancel,
  onDanger,
  closeOnBackdrop = true,
  inline = false,
  className,
}: DialogProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const loading = status === 'loading';

  const setOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOpenState(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );
  const close = useCallback(() => setOpen(false), [setOpen]);

  const footer = showFooter ? (
    <>
      {showCancel ? (
        <Button
          variant="neutral"
          size="sm"
          className={DIALOG_CANCEL_BUTTON_CLASS[size]}
          onClick={() => {
            onCancel?.();
            close();
          }}
        >
          {cancelLabel}
        </Button>
      ) : null}
      <Button
        variant={confirmVariant}
        size="sm"
        status={confirmStatus}
        disabled={confirmDisabled}
        className={DIALOG_MAIN_BUTTON_CLASS[size]}
        style={DIALOG_MAIN_BUTTON_STYLE[size]}
        onClick={onConfirm}
      >
        {confirmLabel}
      </Button>
      {showDanger ? (
        <Button
          variant="danger"
          size="sm"
          className={DIALOG_MAIN_BUTTON_CLASS[size]}
          style={DIALOG_MAIN_BUTTON_STYLE[size]}
          onClick={onDanger}
        >
          {dangerLabel}
        </Button>
      ) : null}
    </>
  ) : undefined;

  return (
    <ModalLayer
      open={open}
      onDismiss={close}
      placement="center"
      layer="modal"
      labelledBy={titleId}
      describedBy={body !== undefined && status !== 'init' ? bodyId : undefined}
      closeOnBackdrop={closeOnBackdrop && !loading}
      closeOnEscape={!loading}
      busy={status !== 'default'}
      inline={inline}
      data-size={size}
      data-status={status}
      className={cn(DIALOG_PANEL_CLASS, DIALOG_WIDTH_CLASS[size], className)}
    >
      <DialogParts
        titleId={titleId}
        bodyId={bodyId}
        title={title}
        body={body}
        content={children}
        status={status}
        onClose={close}
        headerActions={headerActions}
        subheader={subheader}
        footer={footer}
        bodyClassName={bodyClassName}
      />
    </ModalLayer>
  );
}
